import parse from 'html-react-parser';
import { KeyboardEvent, useEffect, useId, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Callout } from '@/components/core/Callout';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import { StepProgress } from '@/components/core/StepProgress';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal';
import { Stepper } from '@/hooks/useStepper';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { DataModelAttribute, Item } from '@/types/DataModel';
import { ServerErrorResponse } from '@/types/ServerErrorResponse';

import { MassUpdateForm } from './component/MassUpdateForm';
import NavigationButtons from './component/NavigationButtons';
import StepsBuilder from './component/StepsBuilder';
import { useCreateSelection, useMassUpdateResource } from './mass-update.queries';
import {
    MASS_UPDATE_ALL_ITEMS_DISABLED_ERROR_CODE,
    MassUpdateAttributeValue,
    Selection,
} from './mass-update.types';
import {
    clearMassUpdateStartToastShown,
    getMassUpdateStartToastMessage,
    markMassUpdateStartToastShown,
    resolveMassUpdateStartCount,
} from './massUpdateStartToast';

interface MassUpdateDialogProps {
    selection: Selection
    item: Item;
    category?: string;
    numberFound?: number;
    maxNumberFound?: number;
    onClose: (toWait?: boolean, completed?: boolean) => void;
}

const MassUpdateDialog = ({
    selection,
    item,
    category,
    numberFound,
    maxNumberFound,
    onClose,
}: MassUpdateDialogProps) => {

    const [selectedAttributes, setSelectedAttributes] = useState<
        DataModelAttribute[]
    >([]);
    const [values, setValues] = useState<Record<string, MassUpdateAttributeValue>>({});
    const [isButtonDisable, setIsButtonDisable] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showConfirmAllDialog, setShowConfirmAllDialog] = useState(false);
    const [confirmPhrase, setConfirmPhrase] = useState('');
    const [showPhraseError, setShowPhraseError] = useState(false);
    const [massUpdateOptions, setMassUpdateOptions] = useState<{
        selectionId: string;
        itemData: Record<string, MassUpdateAttributeValue>;
    }>({
        selectionId: '',
        itemData: {},
    });
    const [open, setOpen] = useState(true);
    const isClosingRef = useRef(false);
    const showConfirmAllDialogRef = useRef(false);
    const { t } = useTranslation();
    const {toast} = useToast();
    const instructionId = useId();
    const errorId = useId();

    useEffect(() => {
        showConfirmAllDialogRef.current = showConfirmAllDialog;
    }, [showConfirmAllDialog]);

    const { mutate: createSelection, isPending: isPendingCreateSelection } =
        useCreateSelection();

    const { mutate: massUpdateResource } =
        useMassUpdateResource();

    const massUpdateSteps = [
        t('MASS_UPDATE_EDITOR.ATTRIBUTE_SELECTION.LABEL'),
        t('MASS_UPDATE_EDITOR.EDIT_ATTRIBUTES.LABEL'),
        t('MASS_UPDATE_EDITOR.SUMMARY.LABEL'),
    ];

    const expectedPhrase = t('MASS_UPDATE_EDITOR.CONFIRM_ALL.PHRASE');
    const trimmedConfirmPhrase = confirmPhrase.trim();
    const isPhraseMatched = trimmedConfirmPhrase === expectedPhrase;

    const handleSelectedAttributesChange = (
        attributes: DataModelAttribute[],
    ) => {
        setSelectedAttributes(attributes);
    };
    const delay = (ms:number) => new Promise(res => setTimeout(res, ms));

    const restoreBrowseUrl = () => {
        try {
            const $location = getInjector().get('$location') as {
                path: () => string;
                displayUrl: (path: string) => void;
            };
            const path = $location.path() || '';
            // Attribute focus during mass-update can leave /edit/new/attribute/... in the bar.
            // Do not touch a real item-editor URL (/edit/<primaryKey>).
            if (path.indexOf('/edit/new') === 0) {
                $location.displayUrl('/browse');
            }
        } catch {
            // Angular bridge unavailable (Storybook / tests) — ignore.
        }
    };

    const onDialogClosed = (toWait = false, completed = false) => {
        if (isClosingRef.current) {
            return;
        }
        isClosingRef.current = true;
        showConfirmAllDialogRef.current = false;
        setShowConfirmAllDialog(false);
        setOpen(false);
        restoreBrowseUrl();
        onClose(toWait, completed);
    };

    const closeConfirmAllDialog = () => {
        showConfirmAllDialogRef.current = false;
        setShowConfirmAllDialog(false);
        setConfirmPhrase('');
        setShowPhraseError(false);
    };

    const executeMassUpdate = (force = false) => {
        setErrorMessage(null);
        // Reserve the start toast before the request so a RUNNING event cannot
        // also toast. Clear first so a previous job does not suppress this one.
        clearMassUpdateStartToastShown();
        const startCount = resolveMassUpdateStartCount({
            primaryKeys: selection.primaryKeys,
            numberFound,
            maxNumberFound,
        });
        if (startCount) {
            markMassUpdateStartToastShown();
        }
        massUpdateResource({
            data: {
                ...massUpdateOptions,
                itemData: values,
                force,
            },
        }, {
            onSuccess: async () => {
                if (startCount) {
                    toast.info(getMassUpdateStartToastMessage(t, startCount));
                }
                setIsButtonDisable(true);
                await delay(1500);
                onDialogClosed(true, true);
            },
            onError: (error) => {
                clearMassUpdateStartToastShown();
                logger.error(error.message);
                const errorData = (error as unknown as ServerErrorResponse)?.response?.data;
                const isAllItemsDisabled =
                    errorData?.errorCode === MASS_UPDATE_ALL_ITEMS_DISABLED_ERROR_CODE ||
                    errorData?.message === 'MASS_UPDATE_EDITOR.RUN.ALL_ITEMS_DISABLED';

                // LAX-10087: BE rejects unrestricted all-items unless force=true (error 1320).
                // Stop here, ask for typed confirmation, then retry with force — do not treat as terminal.
                if (!force && isAllItemsDisabled) {
                    setConfirmPhrase('');
                    setShowPhraseError(false);
                    showConfirmAllDialogRef.current = true;
                    setShowConfirmAllDialog(true);
                    return;
                }

                // Show the (localized) error as a persistent banner inside the dialog instead
                // of a transient toast, and disable the start button so the same failing
                // update cannot be retried. The user closes the dialog manually.
                setErrorMessage(errorData?.message ?? 'MASS_UPDATE_EDITOR.RUN.FAILED_MESSAGE');
                setIsButtonDisable(true);
            },
        });
    };

    const validateAndMaybeShowError = () => {
        if (trimmedConfirmPhrase.length > 0 && !isPhraseMatched) {
            setShowPhraseError(true);
            return false;
        }

        return isPhraseMatched;
    };

    const handleConfirmAllItems = () => {
        if (!isPhraseMatched) {
            if (trimmedConfirmPhrase.length > 0) {
                setShowPhraseError(true);
            }
            return;
        }

        // Keep confirm open until the force request settles so closing it does not
        // tear down the parent mass-update Dialog mid-request.
        executeMassUpdate(true);
    };

    const handleConfirmPhraseKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter') {
            return;
        }

        event.preventDefault();

        if (!validateAndMaybeShowError()) {
            return;
        }

        executeMassUpdate(true);
    };

    useEffect(() => {
        if (isPhraseMatched) {
            setShowPhraseError(false);
        }
    }, [isPhraseMatched]);

    useEffect(() => {
        if (selection) {
            createSelection(
                {
                    selection: selection,
                },
                {
                    onSuccess: (res) => {
                        setMassUpdateOptions((prevOptions) => ({
                            ...prevOptions,
                            selectionId: res.selectionId,
                        }));
                    },
                    onError:(error) =>{
                        logger.error(error.message);
                    }
                },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const phraseDescribedBy = showPhraseError
        ? `${instructionId} ${errorId}`
        : instructionId;

    return (
        <div>
            <Stepper steps={massUpdateSteps}>
                <Dialog
                    open={open}
                    onOpenChange={(nextOpen) => {
                        // Nested ConfirmationModal must not tear down the mass-update dialog
                        // (Radix may notify the parent when the child opens / takes focus).
                        if (!nextOpen && !showConfirmAllDialogRef.current) {
                            onDialogClosed();
                        }
                    }}
                >
                    <DialogContent
                        variant="large"
                        onPointerDownOutside={(e) => e.preventDefault()}
                        onInteractOutside={(e) => e.preventDefault()}
                        onFocusOutside={(e) => {
                            if (showConfirmAllDialog) {
                                e.preventDefault();
                            }
                        }}
                    >
                        <DialogHeader>
                            <DialogHeaderDefault>
                                {t('MASS_UPDATE_EDITOR.HEADER')}
                            </DialogHeaderDefault>
                        </DialogHeader>
                        {isPendingCreateSelection ? (
                            <DialogBody padding="none" className="tw-h-[80vh] tw-flex tw-flex-1 tw-items-center tw-justify-center">
                                <Loader />
                            </DialogBody>
                        ) : (
                            <MassUpdateForm>
                                <DialogBody padding="none" className="tw-flex tw-flex-col tw-min-h-0 tw-h-[80vh] tw-overflow-hidden tw-px-5 lg:tw-px-8 tw-py-6 lg:tw-py-8">
                                    {errorMessage && (
                                        <Callout variant="destructive" canBeClosed={false} className="tw-mb-4">
                                            {t(errorMessage)}
                                        </Callout>
                                    )}
                                    <StepProgress />
                                    <StepsBuilder
                                        item={item}
                                        selectedAttributes={selectedAttributes}
                                        values={values}
                                        primaryKeys={selection.primaryKeys}
                                        category ={category}
                                        setUpdatedValues={(val) => {
                                            setValues(val);
                                        }}
                                        handleSelectedAttributesChange={(
                                            selected: DataModelAttribute[],
                                        ) =>
                                            handleSelectedAttributesChange(selected)
                                        }
                                    />
                                </DialogBody>
                            </MassUpdateForm>
                        )}
                        <DialogFooter>
                            <NavigationButtons
                                selectedAttributes={selectedAttributes}
                                isButtonDisable ={isButtonDisable}
                                runMassUpdate={() => executeMassUpdate(false)}
                            />
                            <DialogClose asChild={true}>
                                <Button
                                    variant="secondary"
                                    onClick={() => onDialogClosed()}
                                >
                                    {t('CLOSE')}
                                </Button>
                            </DialogClose>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </Stepper>
            <ConfirmationModal
                show={showConfirmAllDialog}
                title="MASS_UPDATE_EDITOR.CONFIRM_ALL.TITLE"
                discardBtnText="MASS_UPDATE_EDITOR.CONFIRM_ALL.CONFIRM"
                closeBtnText="MASS_UPDATE_EDITOR.CONFIRM_ALL.CANCEL"
                btnVariant="default"
                isConfirmDisabled={!isPhraseMatched}
                preventDismissOutside={true}
                autoFocusCancel={true}
                asymmetricBodyPadding={true}
                onSubmit={handleConfirmAllItems}
                onClose={closeConfirmAllDialog}
            >
                <Callout variant="warning" canBeClosed={false}>
                    <div className="tw-flex tw-flex-col">
                        <div className="tw-font-bold tw-text-t-base">
                            {t('MASS_UPDATE_EDITOR.SUMMARY.WARNING_ALL_TITLE')}
                        </div>
                        <div className="tw-text-t-base">
                            {parse(t('MASS_UPDATE_EDITOR.SUMMARY.WARNING_ALL'))}
                        </div>
                    </div>
                </Callout>
                <div className="tw-flex tw-flex-col tw-gap-1">
                    <label
                        id={instructionId}
                        htmlFor="mass-update-confirm-all-phrase"
                        className="tw-text-t-base tw-text-t-primary tw-m-0"
                    >
                        {parse(t('MASS_UPDATE_EDITOR.CONFIRM_ALL.INSTRUCTION'))}
                    </label>
                    <Input
                        id="mass-update-confirm-all-phrase"
                        type="text"
                        value={confirmPhrase}
                        autoComplete="off"
                        aria-label={t('MASS_UPDATE_EDITOR.CONFIRM_ALL.INPUT_LABEL')}
                        aria-describedby={phraseDescribedBy}
                        aria-invalid={showPhraseError || undefined}
                        onChange={(event) => setConfirmPhrase(event.target.value)}
                        onBlur={validateAndMaybeShowError}
                        onKeyDown={handleConfirmPhraseKeyDown}
                        data-testid="mass-update-confirm-all-phrase"
                    />
                    {showPhraseError && (
                        <p
                            id={errorId}
                            className="tw-text-t-sm tw-text-error tw-m-0"
                            role="alert"
                            data-testid="mass-update-confirm-all-error"
                        >
                            {t('MASS_UPDATE_EDITOR.CONFIRM_ALL.PHRASE_MISMATCH')}
                        </p>
                    )}
                </div>
            </ConfirmationModal>
        </div>
    );
};

MassUpdateDialog.displayName = 'MassUpdateDialog';

export { MassUpdateDialog, type MassUpdateDialogProps };
